/**
 * Moderation Queue Table Component
 *
 * Displays moderation topics in a table format with severity, cases, activity, and moderator information.
 * Follows the same pattern as AuditSessionsTable for consistency.
 *
 * TODO: When XDS List components are ported to design-system-ve, consider replacing
 * the custom DataList implementation with XDS List, ListItem, and ListDivider components
 * for better accessibility, keyboard navigation, and focus management.
 *
 * TODO: When XDS IconText component is ported, replace the custom change indicator
 * implementation with IconText for better semantic structure.
 */

import { Button, ButtonType, Text } from '@soteria/design-system-ve';

import { DataList, type TableColumn } from '../../../core/components';

import * as styles from './ModerationQueueTable.css';

interface ModerationTopic {
  name: string;
  caseSLA: string;
  openCases: number;
  nearSLA: number;
  pastSLA: number;
}

interface ModerationQueueTableProps {
  topics: ModerationTopic[];
  isLoading: boolean;
  onStartModerate: (topic: ModerationTopic) => void;
}

export function ModerationQueueTable({ topics, isLoading, onStartModerate }: ModerationQueueTableProps) {
  const columns: TableColumn<ModerationTopic>[] = [
    {
      key: 'name',
      header: 'Topic Name',
      render: (topic: ModerationTopic) => <Text className={styles.topicName}>{topic.name}</Text>,
      width: '20%',
    },
    {
      key: 'caseSLA',
      header: 'Case SLA',
      render: (topic: ModerationTopic) => <Text className={styles.statCount}>{topic.caseSLA}</Text>,
      width: '12%',
    },
    {
      key: 'openCases',
      header: 'Open Cases',
      render: (topic: ModerationTopic) => <Text className={styles.statCount}>{topic.openCases}</Text>,
      width: '12%',
    },
    {
      key: 'nearSLA',
      header: 'Near SLA',
      render: (topic: ModerationTopic) => <Text className={topic.nearSLA > 0 ? styles.warningCount : styles.statCount}>{topic.nearSLA}</Text>,
      width: '10%',
    },
    {
      key: 'pastSLA',
      header: 'Past SLA',
      render: (topic: ModerationTopic) => <Text className={topic.pastSLA > 0 ? styles.errorCount : styles.statCount}>{topic.pastSLA}</Text>,
      width: '10%',
    },
    {
      key: 'actions',
      header: '',
      render: (topic: ModerationTopic) => (
        <div className={styles.actionsContainer}>
          {/* TODO: Wrap with XDS Tooltip when ported for better UX:
              <Tooltip content="View detailed case information and take moderation actions">
                <Button ...>Start Moderate</Button>
              </Tooltip>
          */}
          <Button buttonType={ButtonType.Primary} onClick={() => onStartModerate(topic)}>
            Start Moderate
          </Button>
        </div>
      ),
      width: '22%',
    },
  ];

  return <DataList columns={columns} data={topics} dataTypeName="moderation topic" isLoading={isLoading} />;
}
